/**
 * Purchases: the list, the form, and one purchase in full.
 *
 * A purchase is the developer's analogue of a tenancy and behaves nothing like one — a total, a
 * deposit, instalments over years, and an end that is a handover rather than a renewal.
 *
 * **The plan is shown before it is agreed.** The buyer is usually sitting across the desk, and
 * nobody should sign a four-year schedule they have not seen. It is the same idea as the tenancy
 * preview, for a stronger reason.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PURCHASE_FREQUENCIES } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import { minor, titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LinkButton,
  PageHeader,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { SearchSelect } from "@/components/search-select";
import { SearchBox } from "@/components/list-toolbar";
import { useListParams } from "@/lib/list-params";
import { usePropertyChoices, useSoldNoun } from "@/lib/development";
import { resolveParty, searchParties } from "@/lib/pickers";
import { CommissionOverrides, MarketerPicker } from "./Marketers";
import { Refusal } from "@/components/padlock";

const STATUS_TONE: Record<string, "success" | "brand" | "warn" | "muted" | "danger"> = {
  draft: "muted",
  active: "success",
  completed: "brand",
  handed_over: "muted",
  cancelled: "danger",
  defaulted: "danger",
};

/*
  The list moved out, and did not come back.

  Purchases was one of five rail items that were five doors onto the same job — selling a unit to
  somebody — and it is now the Sales screen, `DevelopmentSales.tsx`, where a reservation and a sale
  are one flow with a stage on each row. `/app/development/purchases` redirects there, because that
  address is in people's bookmarks.

  What stays here is the form that records a sale, which is a different thing from a list of them.
*/

/* ------------------------------------------------------------------- the form */

interface Scheduled {
  sequence: number;
  label: string;
  dueDate: string;
  amount: number;
  amountLabel: string;
}

export function PurchaseForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fmt = useFormat();
  const [estateId, setEstateId] = useState(params.get("estateId") ?? "");
  const [partyId, setPartyId] = useState("");
  const [price, setPrice] = useState("");
  const [deposit, setDeposit] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [instalments, setInstalments] = useState("24");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState<{ schedule: Scheduled[]; totalLabel: string } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  /**
   * A plan typed in by hand.
   *
   * The flexible end of the same idea: "₦5m now, ₦10m when the roof goes on, the rest at
   * handover" is a real agreement, and a form that can only express equal monthly thirds pushes
   * it into a spreadsheet. Held here rather than in the DOM because the running total under it
   * has to move as the rows do.
   */
  const [rows, setRows] = useState<Array<{ label: string; dueDate: string; amount: string }>>([
    { label: "", dueDate: "", amount: "" },
  ]);
  const custom = frequency === "custom";

  const { data: estates } = usePropertyChoices();

  const { data: plots } = useQuery({
    queryKey: ["estate", estateId, "plots"],
    queryFn: () =>
      api.get<{ plots: Array<{ id: string; label: string; status: string }>; noun: { one: string; many: string } }>(
        `/estates/${estateId}`,
      ),
    enabled: Boolean(estateId),
  });

  /*
    The word for what is being allocated, from the property that has been chosen.

    Its own scheme's word once there is one — that answer is exact and the server has already made
    it — and the account-wide agreement before then, so the field is not called "unit" on a land
    developer's screen for the two seconds before they pick anything.
  */
  const chosen = (estates?.estates ?? []).find((property) => property.id === estateId);
  const noun = useSoldNoun(chosen?.sells);

  const [plotId, setPlotId] = useState("");

  /*
    Who sold it, and the terms they sold it on.

    **Leaving all three out is the ordinary case**, and the form is built so that it is also the
    easy one: nobody is chosen by default, and the two override boxes are not even drawn until
    somebody has been. Left alone, the rate is resolved from the tree — node, then price type, then
    release, then property — and the basis from the property, both at the moment of sale and both
    **frozen on it from then on**. Re-rating a release next month moves what the next marketer
    earns and cannot reach a deal already done.
  */
  const [marketerId, setMarketerId] = useState("");
  const [commissionBp, setCommissionBp] = useState("");
  const [commissionBasis, setCommissionBasis] = useState("");

  /*
    The schedule, redrawn as the terms change.

    Deliberately not on a button. Somebody changing "24 months" to "36" wants to see what that does
    to the instalment, and a preview you have to ask for is a preview nobody looks at twice.
  */
  /* The rows, as the API wants them. Incomplete ones are dropped rather than sent as zeroes:
     a half-typed row should not make the preview say the plan is short. */
  const typedRows = rows
    .filter((row) => row.dueDate && row.amount)
    .map((row) => ({ label: row.label || undefined, dueDate: row.dueDate, amount: minor(row.amount) ?? 0 }));

  const typedTotal = typedRows.reduce((sum, row) => sum + row.amount, 0);
  const balance = (minor(price) ?? 0) - (minor(deposit) ?? 0);
  const shortfall = balance - typedTotal;

  useEffect(() => {
    const total = minor(price) ?? 0;
    if (!total || !startDate || (custom && !typedRows.length)) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await api.post<{ schedule: Scheduled[]; totalLabel: string }>("/purchases/preview", {
          price: total,
          deposit: minor(deposit) ?? 0,
          frequency,
          instalments: Number(instalments) || 1,
          startDate,
          schedule: custom ? typedRows : undefined,
        });
        if (!cancelled) {
          setPreview(result);
          setPreviewError(null);
        }
      } catch (failure) {
        if (cancelled) return;
        setPreview(null);
        /* The server's own words. It is the one place that knows the parts must add up to the
           price, and re-deriving the sentence here would make the two drift apart. */
        setPreviewError(failure instanceof Error ? failure.message : null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, deposit, frequency, instalments, startDate, custom, JSON.stringify(typedRows)]);

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/purchases", input),
    invalidates: [["purchases"], ["sales"], ["developer-summary"], ["estate", estateId]],
    onSuccess: (result) => navigate(`/app/development/sales/${result.id}`),
  });

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Record a sale"
        back={{ to: "/app/development/sales", label: "Sales" }}
        description="The terms agreed with the buyer, and the schedule those terms produce. Review the schedule with the buyer before recording the sale."
      />

      {save.errorMessage ? (
        <div className="mb-4">
          {/* A refusal that named a plan draws the padlock and the way out of it; everything else
              is the ordinary red box, unchanged. One decision, made in one place. */}
          <Refusal error={save.error} message={save.errorMessage} />
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              const values = formValues(event.currentTarget);
              save.mutate({
                estateId,
                partyId,
                plotId: plotId || null,
                price: minor(price) ?? 0,
                deposit: minor(deposit) ?? 0,
                frequency,
                instalments: Number(instalments) || 1,
                startDate,
                schedule: custom ? typedRows : undefined,
                lateFeeBps: Math.round(Number(values.lateFee || 0) * 100),
                earlyDiscountBps: Math.round(Number(values.earlyDiscount || 0) * 100),
                notes: values.notes || null,
                /* Undefined rather than null: an absent key is "the tree decides", which is not
                   the same answer as "there is no commission on this sale". */
                marketerId: marketerId || undefined,
                commissionBp: commissionBp.trim() === "" ? undefined : Math.round(Number(commissionBp) * 100),
                commissionBasis: commissionBasis || undefined,
              });
            }}
          >
            <Field label="Property" htmlFor="estateId" required>
              <Select id="estateId" name="estateId" value={estateId} onChange={(event) => { setEstateId(event.target.value); setPlotId(""); }} required>
                <option value="">Choose a property…</option>
                {(estates?.estates ?? []).map((estate) => (
                  <option key={estate.id} value={estate.id}>
                    {estate.name}
                  </option>
                ))}
              </Select>
            </Field>

            {/* Optional, and said so plainly: allocation often follows the money, sometimes by
                ballot, and requiring it would force every developer to pre-allocate on day one. */}
            <Field label={titleCase(noun.one)} htmlFor="plotId" hint="Optional. Allocation may be recorded later, once it has been decided.">
              <Select id="plotId" name="plotId" value={plotId} onChange={(event) => setPlotId(event.target.value)} disabled={!estateId}>
                <option value="">Allocate later</option>
                {(plots?.plots ?? [])
                  .filter((plot) => plot.status === "available")
                  .map((plot) => (
                    <option key={plot.id} value={plot.id}>
                      {plot.label}
                    </option>
                  ))}
              </Select>
            </Field>

            <div className="sm:col-span-2">
              <Field label="Buyer" htmlFor="partyId" required>
                <SearchSelect
                  id="partyId"
                  value={partyId}
                  onChange={setPartyId}
                  /* Every client, not only those already flagged as a buyer. A buyer is a role on
                     the same contact list as a tenant and a landlord — the whole point of one
                     application — and the person buying a plot this month may be the one renting a
                     shop from you next year. */
                  search={searchParties()}
                  resolve={resolveParty}
                  placeholder="Search clients…"
                />
              </Field>
            </div>

            <Field label="Agreed price" htmlFor="price" required>
              <MoneyInput id="price" name="price" value={price} onChange={setPrice} required placeholder="25,000,000" />
            </Field>
            <Field label="Deposit" htmlFor="deposit" hint="Payable on signing, ahead of the schedule. For example 5,000,000.">
              <MoneyInput id="deposit" name="deposit" value={deposit} onChange={setDeposit} placeholder="5,000,000" />
            </Field>

            <Field label="Instalment frequency" htmlFor="frequency">
              <Select id="frequency" name="frequency" value={frequency} onChange={(event) => setFrequency(event.target.value)}>
                {PURCHASE_FREQUENCIES.filter((code) => code !== "custom").map((code) => (
                  <option key={code} value={code}>
                    {code.charAt(0).toUpperCase() + code.slice(1)}
                  </option>
                ))}
                {/* The flexible end. Some plans are rigid and some are "when the roof goes on". */}
                <option value="custom">Bespoke — set each payment individually</option>
              </Select>
            </Field>
            {custom ? (
              <Field label="Number of payments" hint="Determined by the schedule entered below.">
                <Input value={typedRows.length || ""} readOnly placeholder="—" />
              </Field>
            ) : (
              <Field label="Number of instalments" htmlFor="instalments" required>
                <Input
                  id="instalments"
                  name="instalments"
                  type="number"
                  min={1}
                  max={240}
                  value={instalments}
                  onChange={(event) => setInstalments(event.target.value)}
                  required
                  placeholder="24"
                />
              </Field>
            )}

            <Field label="First payment due" htmlFor="startDate" required>
              <Input id="startDate" name="startDate" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} required />
            </Field>
            <Field label="Late payment fee" htmlFor="lateFee" hint="A percentage of the amount outstanding on an overdue instalment. For example 2.5.">
              <Input id="lateFee" name="lateFee" type="number" min={0} max={100} step="0.01" placeholder="2.5" />
            </Field>
            <Field label="Early settlement discount" htmlFor="earlyDiscount" hint="A percentage deducted from instalments not yet due. For example 5.">
              <Input id="earlyDiscount" name="earlyDiscount" type="number" min={0} max={100} step="0.01" placeholder="5" />
            </Field>
            <Field label="Notes" htmlFor="notes">
              <Input id="notes" name="notes" placeholder="Terms agreed at the site office." />
            </Field>

            <Field
              label="Sold by"
              htmlFor="marketerId"
              hint="Leave it as nobody where your own office sold it. The rate comes from the property unless it is changed here."
            >
              <MarketerPicker value={marketerId} onChange={setMarketerId} />
            </Field>
            {/* Drawn only once somebody has been chosen. A rate with nobody to earn it is a number
                that goes nowhere, and two empty boxes on every sale form would be two questions
                nobody asked. */}
            {marketerId ? (
              <CommissionOverrides
                bp={commissionBp}
                basis={commissionBasis}
                onBp={setCommissionBp}
                onBasis={setCommissionBasis}
              />
            ) : null}

            {custom ? (
              <div className="sm:col-span-2">
                <Field
                  label="Scheduled payments"
                  group
                  hint="Any dates and amounts. The total must equal the price less the deposit. For example 5,000,000 on completion of the roof."
                >
                  <div className="space-y-2">
                    {rows.map((row, index) => (
                      <div key={index} className="flex flex-wrap items-end gap-2">
                        <div className="min-w-[9rem] flex-1">
                          <Input
                            aria-label={`What payment ${index + 1} is for`}
                            placeholder="On completion of the roof"
                            value={row.label}
                            onChange={(event) =>
                              setRows((current) =>
                                current.map((entry, at) => (at === index ? { ...entry, label: event.target.value } : entry)),
                              )
                            }
                          />
                        </div>
                        <div className="w-40">
                          <Input
                            aria-label={`Payment ${index + 1} due`}
                            type="date"
                            value={row.dueDate}
                            onChange={(event) =>
                              setRows((current) =>
                                current.map((entry, at) => (at === index ? { ...entry, dueDate: event.target.value } : entry)),
                              )
                            }
                          />
                        </div>
                        <div className="w-40">
                          <MoneyInput
                            aria-label={`Payment ${index + 1} amount`}
                            placeholder="5,000,000"
                            value={row.amount}
                            onChange={(next) =>
                              setRows((current) =>
                                current.map((entry, at) => (at === index ? { ...entry, amount: next } : entry)),
                              )
                            }
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setRows((current) => current.filter((_, at) => at !== index))}
                          disabled={rows.length === 1}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}

                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setRows((current) => [...current, { label: "", dueDate: "", amount: "" }])}
                      >
                        Add a payment
                      </Button>
                      {/* The running total, and what is missing. Without it somebody types nine
                          rows, presses save, and is told the ninth is wrong by a hundred naira. */}
                      {balance > 0 ? (
                        <span className={`text-sm ${shortfall === 0 ? "text-success-700" : "text-ink-500"}`}>
                          {shortfall === 0
                            ? "The schedule balances."
                            : shortfall > 0
                              ? `${fmt.money(shortfall)} remains unallocated.`
                              : `${fmt.money(-shortfall)} in excess of the balance.`}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </Field>
              </div>
            ) : null}

            <div className="sm:col-span-2">
              <Button type="submit" disabled={save.isPending || !estateId || !partyId || !price}>
                {save.isPending ? "Saving…" : "Record the sale"}
              </Button>
            </div>
          </form>
        </Card>

        <Card>
          <CardTitle hint="The schedule these terms produce. Review it with the buyer before recording the sale.">Payment schedule</CardTitle>
          {previewError ? (
            <p className="text-sm text-danger-600">{previewError}</p>
          ) : !preview ? (
            <p className="text-sm text-ink-500">
              {custom ? "Enter the payments and the schedule appears here." : "Enter a price and the schedule appears here."}
            </p>
          ) : (
            <>
              <p className="mb-3 text-sm text-ink-600">
                {preview.schedule.length} payments, {preview.totalLabel} in total.
              </p>
              <ul className="max-h-[420px] space-y-1 overflow-y-auto text-sm">
                {preview.schedule.map((row) => (
                  <li key={row.sequence} className="flex justify-between gap-3 border-b border-ink-100 py-1.5 last:border-0">
                    <span className="text-ink-600">
                      {row.label}
                      <span className="block text-xs text-ink-400">{row.dueDate}</span>
                    </span>
                    <span className="num font-medium">{row.amountLabel}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
